import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Zap, Store, CreditCard, Smartphone, Check,
  ArrowRight, ArrowLeft, Loader2, ExternalLink
} from 'lucide-react';
import api from '@/lib/api';

const STEPS = [
  { id: 'welcome', label: 'Welcome', icon: Zap },
  { id: 'store', label: 'Store Setup', icon: Store },
  { id: 'stripe', label: 'Connect Stripe', icon: CreditCard },
  { id: 'ready', label: 'Ready!', icon: Smartphone },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);

  const [storeName, setStoreName] = useState('');
  const [storeAddress, setStoreAddress] = useState('');
  const [storePhone, setStorePhone] = useState('');

  const [onboardingStatus, setOnboardingStatus] = useState(null);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const [statusRes, storeRes] = await Promise.all([
          api.get('/onboarding/status'),
          api.get('/store/profile').catch(() => null)
        ]);
        setOnboardingStatus(statusRes.data);

        if (storeRes?.data) {
          setStoreName(storeRes.data.name || '');
          setStoreAddress(storeRes.data.address || '');
          setStorePhone(storeRes.data.phone || '');
        }

        // Auto-advance to the right step
        if (statusRes.data.steps.store_setup) setStep(2);
        if (statusRes.data.steps.stripe_connected) setStep(3);
        if (statusRes.data.complete) {
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch {
        // Ignore
      } finally {
        setLoading(false);
      }
    };
    checkStatus();
  }, [navigate]);

  const handleSaveStore = async () => {
    if (!storeName.trim()) {
      toast.error('Store name is required');
      return;
    }
    setSaving(true);
    try {
      await api.put('/store/profile', {
        name: storeName.trim(),
        address: storeAddress.trim(),
        phone: storePhone.trim(),
      });
      toast.success('Store profile saved!');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      const res = await api.post('/stripe/connect', { return_url: window.location.origin });
      if (res.data.already_connected) {
        toast.success('Stripe already connected!');
        setStep(3);
      } else if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start Stripe onboarding');
    } finally {
      setConnectingStripe(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] text-white" data-testid="onboarding-wizard">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#09090b]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-5 h-5 text-orange-500" />
            <span className="text-sm font-bold tracking-tighter" style={{ fontFamily: 'Manrope, sans-serif' }}>EmergencyPOS</span>
          </div>
          <div className="flex items-center gap-1" data-testid="onboarding-progress">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center flex-1">
                <div
                  className={`h-1 w-full transition-colors ${
                    i <= step ? 'bg-orange-500' : 'bg-zinc-800'
                  }`}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            {STEPS.map((s, i) => (
              <div key={s.id} className={`text-xs font-medium ${i <= step ? 'text-orange-400' : 'text-zinc-600'}`}>
                {s.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="pt-28 pb-16 px-4 max-w-xl mx-auto">
        {/* Step 0: Welcome */}
        {step === 0 && (
          <div className="text-center animate-fade-in-up" data-testid="onboarding-step-welcome">
            <div className="w-20 h-20 mx-auto bg-orange-500/10 flex items-center justify-center mb-6">
              <Zap className="w-10 h-10 text-orange-500" />
            </div>
            <h1 className="text-3xl font-extrabold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome to EmergencyPOS
            </h1>
            <p className="text-zinc-400 mb-8 max-w-md mx-auto leading-relaxed">
              Let's get your store ready to accept emergency payments. This takes less than 5 minutes.
            </p>
            <div className="space-y-3 text-left bg-zinc-900 border border-zinc-800 p-6 mb-8">
              <div className="text-sm font-bold text-zinc-300 mb-4">Here's what we'll do:</div>
              {[
                'Set up your store profile',
                'Connect your Stripe account',
                'You\'re ready to accept payments!'
              ].map((text, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-orange-500">{i + 1}</span>
                  </div>
                  <span className="text-sm text-zinc-300">{text}</span>
                </div>
              ))}
            </div>
            <Button
              onClick={() => setStep(1)}
              className="h-13 px-10 bg-orange-500 hover:bg-orange-600 text-white font-bold text-base"
              data-testid="onboarding-start-btn"
            >
              Let's go <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 1: Store Setup */}
        {step === 1 && (
          <div className="animate-fade-in-up" data-testid="onboarding-step-store">
            <h2 className="text-2xl font-extrabold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Set up your store
            </h2>
            <p className="text-zinc-400 mb-8">Tell us about your business. You can update these later.</p>

            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm font-medium">Store name *</Label>
                <Input
                  value={storeName}
                  onChange={e => setStoreName(e.target.value)}
                  placeholder="e.g. Pharmacie du Centre"
                  className="h-12 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
                  data-testid="onboarding-store-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm font-medium">Address</Label>
                <Input
                  value={storeAddress}
                  onChange={e => setStoreAddress(e.target.value)}
                  placeholder="e.g. 12 rue de la Paix, Paris"
                  className="h-12 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
                  data-testid="onboarding-store-address"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm font-medium">Phone</Label>
                <Input
                  value={storePhone}
                  onChange={e => setStorePhone(e.target.value)}
                  placeholder="e.g. +33 1 23 45 67 89"
                  className="h-12 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
                  data-testid="onboarding-store-phone"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <Button
                variant="outline"
                onClick={() => setStep(0)}
                className="h-12 px-6 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={handleSaveStore}
                disabled={saving || !storeName.trim()}
                className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold"
                data-testid="onboarding-save-store-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {saving ? 'Saving...' : 'Continue'}
                {!saving && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Stripe Connect */}
        {step === 2 && (
          <div className="text-center animate-fade-in-up" data-testid="onboarding-step-stripe">
            <div className="w-20 h-20 mx-auto bg-[#635BFF]/10 flex items-center justify-center mb-6">
              <CreditCard className="w-10 h-10 text-[#635BFF]" />
            </div>
            <h2 className="text-2xl font-extrabold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Connect your Stripe account
            </h2>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto leading-relaxed">
              Link your Stripe account so payments go directly to you. EmergencyPOS never holds your funds.
            </p>

            <div className="bg-zinc-900 border border-zinc-800 p-5 mb-8 text-left">
              <div className="text-sm font-bold text-zinc-300 mb-3">What happens:</div>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  You'll be redirected to Stripe's secure onboarding
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  Payments go directly to your bank account
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  Standard Stripe processing fees apply
                </li>
              </ul>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={handleConnectStripe}
                disabled={connectingStripe}
                className="h-13 bg-[#635BFF] hover:bg-[#5851DB] text-white font-bold text-base"
                data-testid="onboarding-connect-stripe-btn"
              >
                {connectingStripe ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                {connectingStripe ? 'Connecting...' : 'Connect with Stripe'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setStep(3)}
                className="text-zinc-500 hover:text-zinc-300 text-sm"
                data-testid="onboarding-skip-stripe-btn"
              >
                Skip for now — I'll connect later
              </Button>
            </div>

            <Button
              variant="outline"
              onClick={() => setStep(1)}
              className="mt-4 h-10 border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </div>
        )}

        {/* Step 3: Ready */}
        {step === 3 && (
          <div className="text-center animate-fade-in-up" data-testid="onboarding-step-ready">
            <div className="w-20 h-20 mx-auto bg-emerald-500/10 flex items-center justify-center mb-6">
              <Check className="w-10 h-10 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-extrabold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              You're all set!
            </h2>
            <p className="text-zinc-400 mb-8 max-w-md mx-auto leading-relaxed">
              Your store is ready. Start accepting emergency payments right now.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              <div className="bg-zinc-900 border border-zinc-800 p-5 text-left">
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-2">Store</div>
                <div className="text-sm font-medium text-white">{storeName || 'Your Store'}</div>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-5 text-left">
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-2">Stripe</div>
                <div className="text-sm font-medium">
                  {onboardingStatus?.steps?.stripe_connected ? (
                    <span className="text-emerald-400">Connected</span>
                  ) : (
                    <span className="text-amber-400">Not connected yet</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/payment')}
                className="h-13 bg-orange-500 hover:bg-orange-600 text-white font-bold text-base"
                data-testid="onboarding-create-payment-btn"
              >
                Create my first payment <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="h-11 border-zinc-700 text-zinc-300 hover:bg-zinc-800 font-medium"
                data-testid="onboarding-go-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
